import { useCallback, useEffect, useRef, useState } from "react";

export function formatElapsedMmSs(ms) {
    if (ms == null || Number.isNaN(ms) || ms < 0) ms = 0;
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Running play timer with pause when the app/tab is hidden (iOS background, menu overlay).
 * @param {{ active: boolean, resetKey?: string|number }} options — `resetKey` change resets the clock (new puzzle / reshuffle).
 */
export function usePlaySessionTimer({ active, resetKey = 0 }) {
    const [elapsedMs, setElapsedMs] = useState(0);
    const [paused, setPaused] = useState(false);
    const [manualPaused, setManualPaused] = useState(false);
    const startedAtRef = useRef(null);
    const pausedTotalRef = useRef(0);
    const pauseStartedRef = useRef(null);

    const reset = useCallback(() => {
        startedAtRef.current = null;
        pausedTotalRef.current = 0;
        pauseStartedRef.current = null;
        setManualPaused(false);
        setPaused(false);
        setElapsedMs(0);
    }, []);

    useEffect(() => {
        reset();
    }, [resetKey, reset]);

    const pause = useCallback(() => {
        if (pauseStartedRef.current != null) return;
        pauseStartedRef.current = Date.now();
        if (startedAtRef.current != null) {
            setElapsedMs(
                pauseStartedRef.current - startedAtRef.current - pausedTotalRef.current
            );
        }
        setPaused(true);
    }, []);

    const resume = useCallback(() => {
        if (pauseStartedRef.current != null) {
            pausedTotalRef.current += Date.now() - pauseStartedRef.current;
            pauseStartedRef.current = null;
        }
        setPaused(false);
    }, []);

    const manualPausedRef = useRef(false);

    useEffect(() => {
        manualPausedRef.current = manualPaused;
    }, [manualPaused]);

    const toggleManualPause = useCallback(() => {
        if (manualPausedRef.current) {
            resume();
            setManualPaused(false);
        } else {
            pause();
            setManualPaused(true);
        }
    }, [pause, resume]);

    /** Clear a user pause when they move a tile — timer runs again. */
    const resumeFromUserMove = useCallback(() => {
        if (!manualPausedRef.current) return;
        resume();
        setManualPaused(false);
    }, [resume]);

    useEffect(() => {
        if (!active) {
            pause();
            return undefined;
        }
        if (!manualPausedRef.current) {
            resume();
        }
        if (startedAtRef.current == null) {
            startedAtRef.current = Date.now();
        }

        const id = window.setInterval(() => {
            if (pauseStartedRef.current != null) return;
            const start = startedAtRef.current ?? Date.now();
            setElapsedMs(Date.now() - start - pausedTotalRef.current);
        }, 250);

        return () => window.clearInterval(id);
    }, [active, manualPaused, pause, resume, resetKey]);

    useEffect(() => {
        if (!active) return undefined;

        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                pause();
            } else if (!manualPausedRef.current) {
                resume();
            }
        };

        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [active, manualPaused, pause, resume]);

    const getElapsedMs = useCallback(() => {
        if (startedAtRef.current == null) return 0;
        if (pauseStartedRef.current != null) {
            return pauseStartedRef.current - startedAtRef.current - pausedTotalRef.current;
        }
        return Date.now() - startedAtRef.current - pausedTotalRef.current;
    }, []);

    return {
        elapsedMs,
        formatted: formatElapsedMmSs(elapsedMs),
        paused,
        manualPaused,
        toggleManualPause,
        resumeFromUserMove,
        pause,
        resume,
        getElapsedMs,
        reset,
    };
}
